import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BlockIcon from '@mui/icons-material/Block';
import { alpha, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { blockerReason } from '../../../../util/blockerUtil';

/** "HH:MM" for a raw booking timestamp. */
const hhmm = value => {
  const date = new Date(value ?? '');
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

/**
 * A blocked time range on the timeline.
 *
 * Blockers are internal bookings with status `blocked`; they occupy a grid row
 * like any booking, but they are not customer bookings. They get a hatched,
 * muted card naming the reason instead of a booking card carrying the synthetic
 * guest name ("Timeslot Blocker"). Clicking it opens the booking details, where
 * the blocker can be cancelled.
 */
function BlockerCardBase({
  booking,
  rowHeight = 45,
  topOffset = 40,
  slotWidthPx = 140,
  minutesPerSlot = 30,
  onCardClick,
}) {
  const theme = useTheme();
  const { _id, startTime, endTime, eventDurationMS, comment, resourceId, blockedTimeframe, i } =
    booking;

  const start = new Date(startTime);
  const hourWidthPx = slotWidthPx * (60 / minutesPerSlot);
  const gridOriginPx = 27; // must match BookingGridBase grid origin
  const durationHours = (Number(eventDurationMS) || 0) / 1000 / 60 / 60;
  const left =
    (start.getHours() - 9) * hourWidthPx + (start.getMinutes() / 60) * hourWidthPx + gridOriginPx;
  const width = hourWidthPx * durationHours - 5;
  const rowIndex = Math.max(1, Number(i) || 1);

  const reason = blockerReason(comment);
  const range = `${hhmm(startTime)}–${hhmm(endTime)}`;
  const resourceName = resourceId && typeof resourceId === 'object' ? resourceId.name : null;
  const hatchColor = alpha(theme.palette.text.primary, 0.18);

  const tooltip = [
    reason ? `Grund: ${reason}` : 'Ohne Grund angelegt',
    `Zeitraum: ${blockedTimeframe || range}`,
    resourceName ? `Raum: ${resourceName}` : null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>} placement='top'>
      <Card
        data-booking-id={String(_id)}
        data-testid={`blocker-card-${String(_id)}`}
        variant='outlined'
        onClick={() => onCardClick?.(_id)}
        sx={{
          position: 'absolute',
          left,
          top: topOffset + (rowIndex - 1) * rowHeight,
          width,
          height: `${rowHeight - 6}px`,
          zIndex: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: alpha(theme.palette.text.secondary, 0.5),
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.secondary,
          contentVisibility: 'auto',
          containIntrinsicSize: `${Math.max(0, Math.round(Number(width) || 0))}px ${
            rowHeight - 6
          }px`,
          '&:hover': {
            borderColor: theme.palette.text.secondary,
            color: theme.palette.text.primary,
          },
        }}
      >
        {/* Diagonal hatching: a blocked range must read as "not bookable" at a
            glance, without competing with the booking cards for colour. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage: `repeating-linear-gradient(-45deg, transparent, transparent 6px, ${hatchColor} 6px, ${hatchColor} 12px)`,
          }}
        />
        <BlockIcon fontSize='small' sx={{ position: 'relative', zIndex: 2, flexShrink: 0 }} />
        <Box sx={{ position: 'relative', zIndex: 2, minWidth: 0, lineHeight: 1.15 }}>
          <Typography
            variant='body2'
            noWrap
            sx={{ fontWeight: 'bold', display: 'block', lineHeight: 1.15 }}
          >
            Blocker{reason ? <span style={{ fontWeight: 400 }}> · {reason}</span> : null}
          </Typography>
          <Typography variant='caption' noWrap sx={{ display: 'block', lineHeight: 1.15 }}>
            {range}
            {resourceName ? ` - ${resourceName}` : ''}
          </Typography>
        </Box>
      </Card>
    </Tooltip>
  );
}

BlockerCardBase.propTypes = {
  booking: PropTypes.object.isRequired,
  rowHeight: PropTypes.number,
  topOffset: PropTypes.number,
  slotWidthPx: PropTypes.number,
  minutesPerSlot: PropTypes.number,
  onCardClick: PropTypes.func,
};

export default BlockerCardBase;
